import { format } from "date-fns";

import type { PhaseRow } from "@/infrastructure/supabase/database.types";
import { cn } from "@monorepo-apps/ui/src/lib/utils";

interface WeekStripProps {
  phases: PhaseRow[];
  currentNumber: number;
  size?: "sm" | "md";
}

/**
 * Mission-control 4-cell phase indicator. Past weeks fill solid teal,
 * the current week glows cyan with a status-pulse marker, future weeks
 * are dashed outlines.
 */
export function WeekStrip({ phases, currentNumber, size = "md" }: WeekStripProps) {
  const sorted = [...phases].sort((a, b) => a.number - b.number);
  return (
    <div data-slot="week-strip" className="flex w-full flex-col gap-3">
      <div className={cn("relative grid grid-cols-4 gap-2", size === "sm" ? "h-9" : "h-12")}>
        {sorted.map((p) => {
          const isCurrent = p.number === currentNumber;
          const isPast = p.number < currentNumber;
          return (
            <div key={p.id} className="relative flex flex-col">
              <div
                className={cn(
                  "h-full overflow-hidden rounded-md transition-all",
                  isCurrent &&
                    "shadow-[0_0_0_3px_oklch(0.55_0.17_190_/_0.08),_0_8px_22px_-10px_oklch(0.55_0.17_190_/_0.45)] ring-1 ring-[oklch(0.55_0.17_190_/_0.35)]",
                  isPast &&
                    "bg-[oklch(0.55_0.17_190_/_0.18)] ring-1 ring-[oklch(0.55_0.17_190_/_0.25)]",
                  !isCurrent && !isPast && "border-border border border-dashed"
                )}
                style={
                  isCurrent
                    ? {
                        background:
                          "linear-gradient(135deg, oklch(0.72 0.17 190) 0%, oklch(0.55 0.17 190) 60%, oklch(0.45 0.18 195) 100%)",
                      }
                    : undefined
                }
              >
                {isCurrent ? (
                  <span
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                      background:
                        "radial-gradient(ellipse 80% 60% at 30% 20%, oklch(1 0 0 / 0.30), transparent 70%)",
                    }}
                  />
                ) : null}
                {isPast ? (
                  <span aria-hidden className="absolute inset-0 flex items-center justify-center">
                    <span className="size-1 rounded-full bg-[oklch(0.55_0.17_190)]" />
                  </span>
                ) : null}
              </div>
              {isCurrent ? (
                <span
                  aria-hidden
                  className="pointer-events-none absolute -top-[22px] left-0 flex items-center gap-1.5"
                >
                  <span className="animate-status-pulse size-1.5 rounded-full bg-[oklch(0.55_0.17_190)]" />
                  <span className="font-data text-[10px] font-semibold tracking-[0.18em] text-[oklch(0.45_0.18_195)] uppercase">
                    Now
                  </span>
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-4 gap-2 text-[10px] tracking-[0.16em] uppercase">
        {sorted.map((p) => {
          const isCurrent = p.number === currentNumber;
          return (
            <div
              key={p.id}
              className={cn(
                "flex items-baseline justify-between gap-2",
                isCurrent ? "text-foreground" : "text-muted-foreground"
              )}
            >
              <span className="font-data">W{p.number}</span>
              <span className="font-data">{format(new Date(p.week_of), "d MMM")}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
